(() => {
  "use strict";

  if (window.FFPlantGuide && typeof window.FFPlantGuide.init === "function") {
    window.FFPlantGuide.init();
    return;
  }

  const GUIDE_SELECTOR = "[data-ff-plant-guide]";
  const LINK_SELECTOR = "[data-ff-plant-guide-link]";
  const guideCleanups = new WeakMap();
  let locationFrame = 0;

  function decodeHash(hash) {
    if (!hash || hash.length < 2) return "";

    try {
      return decodeURIComponent(hash.slice(1));
    } catch (_) {
      return hash.slice(1);
    }
  }

  function findTarget(guide, hash) {
    const id = decodeHash(hash);
    if (!id) return null;

    const candidate = document.getElementById(id);
    return candidate && guide.contains(candidate) ? candidate : null;
  }

  function revealTarget(target) {
    if (target instanceof HTMLDetailsElement) target.open = true;
  }

  function scrollToTarget(target, moveFocus) {
    revealTarget(target);

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const summary = target instanceof HTMLDetailsElement
      ? target.querySelector("summary")
      : null;
    const panel = summary ? target.querySelector(".ff-plant-guide__panel") : null;
    const focusTarget = summary && window.getComputedStyle(summary).display !== "none"
      ? summary
      : panel || target;

    requestAnimationFrame(() => {
      // Reserve the sticky header even while it is translated out of view.
      // It can reappear after scrolling, above the desktop purchase bar.
      const header = document.querySelector("header.shopify-section--header");
      const headerPosition = header && window.getComputedStyle(header).position;
      const headerHeight = header && (headerPosition === "sticky" || headerPosition === "fixed")
        ? Math.ceil(header.getBoundingClientRect().height)
        : 0;
      target.style.setProperty("--ff-plant-guide-header-height", `${headerHeight}px`);

      // The purchase bar moves to the top on desktop and may appear during this scroll.
      const productSection = target.closest(".shopify-section--ff-main-product");
      const purchaseBar = productSection && productSection.querySelector("product-sticky-bar");
      const purchaseBarHeight = window.matchMedia("(min-width: 700px)").matches && purchaseBar
        ? Math.ceil(purchaseBar.getBoundingClientRect().height)
        : 0;
      target.style.setProperty("--ff-plant-guide-sticky-bar-height", `${purchaseBarHeight}px`);

      target.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start"
      });

      if (!moveFocus || !focusTarget || typeof focusTarget.focus !== "function") return;

      try {
        focusTarget.focus({ preventScroll: true });
      } catch (_) {
        focusTarget.focus();
      }
    });
  }

  function setupGuide(guide) {
    if (guide.dataset.ffPlantGuideInit === "true") return;
    guide.dataset.ffPlantGuideInit = "true";
    guide.classList.add("is-enhanced");

    const desktopQuery = window.matchMedia("(min-width: 700px)");
    const guideNav = guide.querySelector("[data-ff-plant-guide-nav-disclosure]");
    const guideDisclosures = guide.querySelectorAll("[data-ff-plant-guide-disclosure]");
    let initialScrollTimer = 0;

    const syncGuideNav = () => {
      if (guideNav instanceof HTMLDetailsElement) guideNav.open = desktopQuery.matches;

      guideDisclosures.forEach((disclosure) => {
        if (!(disclosure instanceof HTMLDetailsElement)) return;

        disclosure.open = desktopQuery.matches || disclosure.hasAttribute("data-ff-plant-guide-mobile-open");
      });
    };

    syncGuideNav();

    if (typeof desktopQuery.addEventListener === "function") {
      desktopQuery.addEventListener("change", syncGuideNav);
    } else if (typeof desktopQuery.addListener === "function") {
      desktopQuery.addListener(syncGuideNav);
    }

    const handleGuideClick = (event) => {
      const link = event.target.closest(LINK_SELECTOR);
      if (!link || !guide.contains(link)) return;

      const url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname || url.search !== window.location.search) return;

      const target = findTarget(guide, url.hash);
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();

      if (window.location.hash !== url.hash) {
        window.history.pushState(null, "", url.hash);
      }

      const mobileNav = link.closest("[data-ff-plant-guide-nav-disclosure]");
      if (mobileNav instanceof HTMLDetailsElement && !desktopQuery.matches) mobileNav.open = false;

      scrollToTarget(target, true);
    };

    guide.addEventListener("click", handleGuideClick);

    const initialTarget = findTarget(guide, window.location.hash);
    if (initialTarget) {
      revealTarget(initialTarget);
      initialScrollTimer = window.setTimeout(() => scrollToTarget(initialTarget, false), 30);
    }

    guideCleanups.set(guide, () => {
      if (initialScrollTimer) window.clearTimeout(initialScrollTimer);
      guide.removeEventListener("click", handleGuideClick);

      if (typeof desktopQuery.removeEventListener === "function") {
        desktopQuery.removeEventListener("change", syncGuideNav);
      } else if (typeof desktopQuery.removeListener === "function") {
        desktopQuery.removeListener(syncGuideNav);
      }

      guide.classList.remove("is-enhanced");
      delete guide.dataset.ffPlantGuideInit;
      guideCleanups.delete(guide);
    });
  }

  function init(root = document) {
    if (root.matches && root.matches(GUIDE_SELECTOR)) setupGuide(root);
    root.querySelectorAll(GUIDE_SELECTOR).forEach(setupGuide);
  }

  function destroy(root) {
    if (root.matches && root.matches(GUIDE_SELECTOR)) {
      const cleanup = guideCleanups.get(root);
      if (cleanup) cleanup();
    }

    root.querySelectorAll(GUIDE_SELECTOR).forEach((guide) => {
      const cleanup = guideCleanups.get(guide);
      if (cleanup) cleanup();
    });
  }

  function revealLocationTarget() {
    document.querySelectorAll(GUIDE_SELECTOR).forEach((guide) => {
      const target = findTarget(guide, window.location.hash);
      if (target) scrollToTarget(target, false);
    });
  }

  function scheduleRevealLocationTarget() {
    if (locationFrame) return;

    locationFrame = window.requestAnimationFrame(() => {
      locationFrame = 0;
      revealLocationTarget();
    });
  }

  window.FFPlantGuide = { init, destroy };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
  window.addEventListener("hashchange", scheduleRevealLocationTarget);
  window.addEventListener("popstate", scheduleRevealLocationTarget);
})();
